import React, { createRef, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslations } from "use-intl";
import { useRouter } from "next/router";
import { sanitize } from "utils/sanitize";
import Recaptcha from "components/Recaptcha";
export function Form({
  schema,
  defaultValues,
  children,
  onSubmit,
  loading,
  recaptchaAction = "submit",
  recaptchaSiteKey,
  ...rest
}) {
  const router = useRouter();
  const s = useTranslations("Shared");
  const methods = useForm({
    defaultValues,
    resolver: yupResolver(schema),
    shouldUnregister: true,
    reValidateMode: "onChange"
  });
  const {
    handleSubmit,
    watch
  } = methods;
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Prevent unnecessary token resets
  const tokenRef = React.useRef(null);

  // Watch all fields
  const formValues = watch();

  // Get list of visible form field names from children
  const visibleFieldNames = React.Children.map(children, child => child.props.name ? child.props.name : null).filter(Boolean);

  // Check if all visible fields have some value
  const hasAllVisibleFields = visibleFieldNames.every(fieldName => {
    const value = formValues[fieldName];
    return value && value.toString().trim() !== '';
  });
  async function onRecaptchaChange(token) {
    if (token) {
      tokenRef.current = token; // Store token in ref
      setRecaptchaToken(token);
    }
  }

  // Restore token from ref if state is lost
  useEffect(() => {
    if (!recaptchaToken && tokenRef.current) {
      setRecaptchaToken(tokenRef.current);
    }
  }, [recaptchaToken]);

  // Clean up only on actual unmount, not re-renders
  useEffect(() => {
    return () => {
      // Only clear ref on actual unmount
      tokenRef.current = null;
    };
  }, []);
  const onFormSubmit = handleSubmit(async values => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      // Use token from ref if state is null
      const currentToken = recaptchaToken || tokenRef.current;
      await onSubmit(sanitize(values), currentToken);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  });
  return <form onSubmit={onFormSubmit} {...rest}>
      {React.Children.map(children, child => {
      return child.props.name ? React.createElement(child.type, {
        ...{
          ...child.props,
          register: methods.register,
          errors: methods.formState.errors,
          key: child.props.name
        }
      }) : child;
    })}
      <Recaptcha onRecaptchaChange={onRecaptchaChange} action={recaptchaAction} recaptchaSiteKey={recaptchaSiteKey} />

      <button type="submit" disabled={isSubmitting || !recaptchaToken || !hasAllVisibleFields} aria-disabled={isSubmitting || !recaptchaToken || !hasAllVisibleFields} className="usa-button mt-6">
        {loading || isSubmitting ? s("loading") : s("submit")}
      </button>
    </form>;
}